import React, { useState } from 'react';
import { FACEBOOK_COMMUNITY_URL } from '../../config/constants';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import StepContent from '@mui/material/StepContent';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Check from '@mui/icons-material/Check';
import StepIcon from '@mui/material/StepIcon';
import { styled } from '@mui/material/styles';
import Popover from '@mui/material/Popover';
import {
	COLOR_GREEN,
	COLOR_BROWN_SECONDARY,
	COLOR_SECONDARY_BUTTON,
	COLOR_ICON_INACTIVE,
	GRADIENT_WARM,
	SHADOW_IMAGE_CARD,
} from '../../styles/tokens';
import { buttonTypographySx } from '../../styles/patterns';

const CustomStepIcon = styled(StepIcon)(({ theme, active }) => ({
	fontSize: '42px',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	'& .MuiStepIcon-text': {
		fontSize: '1.2rem',
	},
}));

const stepLabelTypographySx = {
	color: COLOR_BROWN_SECONDARY,
	fontWeight: 500,
	letterSpacing: '1px',
	textAlign: 'left',
};

const FIMEPopover = () => {
	const [anchorFIME, setAnchorFIME] = useState(null);

	const handleFIMEClick = (event) => {
		setAnchorFIME(event.currentTarget);
	};
	const handleFIMEClose = () => {
		setAnchorFIME(null);
	};
	const isOpen = Boolean(anchorFIME);
	const id = isOpen ? 'fime-popover' : undefined;

	return (
		<>
			<button
				type="button"
				style={{
					background: 'none',
					border: 'none',
					padding: 0,
					color: COLOR_GREEN,
					cursor: 'pointer',
					fontFamily: 'Dongle',
					fontSize: 'inherit',
				}}
				onClick={handleFIMEClick}
			>
				Image
			</button>
			<Popover
				id={id}
				open={isOpen}
				anchorEl={anchorFIME}
				onClose={handleFIMEClose}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'center',
				}}
			>
				<img
					src={process.env.PUBLIC_URL + 'images/FIME.jpg'}
					alt="FIME folding tool for fore edge book art strips"
					style={{ maxWidth: '250px', marginBottom: '-10px' }}
				/>
			</Popover>
		</>
	);
};

const STEPS = [
	{
		title: 'Materials Needed',
		description: (
			<ul style={{ paddingLeft: '20px' }}>
				<li>
					Fore Edge Book Art Pattern - Start the{' '}
					<a href="/patternmaker" style={{ color: COLOR_GREEN }}>
						Pattern Maker
					</a>
				</li>
				<li>Inkjet Printer</li>
				<li>Paper - we suggest using 80-100gsm or 28lb paper</li>
				<li>Paper cutter or scissors</li>
				<li>Book</li>
			</ul>
		),
	},
	{
		title: 'Print Your Pattern',
		description: (
			<ul style={{ paddingLeft: '20px' }}>
				<li>Print your pattern at 100% or 'Actual size'</li>
				<li>Ensure 'Fit to page' is unchecked</li>
				<li>Print in Portrait Mode</li>
			</ul>
		),
		image: `${process.env.PUBLIC_URL}/images/1-Print.jpg`,
	},
	{
		title: 'Cut the Strips',
		description: (
			<ul style={{ paddingLeft: '20px' }}>
				<li>Trim the top and bottom edges of each page</li>
				<li>Cut strips along the long solid lines</li>
			</ul>
		),
		image: `${process.env.PUBLIC_URL}/images/2-Cut.jpg`,
	},
	{
		title: 'Fold the Strips',
		description: (
			<ul style={{ paddingLeft: '20px' }}>
				<li>Fold each strip in half lengthwise</li>
				<li>
					Try the{' '}
					<a
						href={FACEBOOK_COMMUNITY_URL}
						target="_blank"
						rel="noopener noreferrer"
						style={{ color: COLOR_GREEN }}
					>
						FIME
					</a>
					-a handy tool designed by Monica Osborne specifically for
					our Pattern Maker. (<FIMEPopover />)
				</li>
			</ul>
		),
		image: `${process.env.PUBLIC_URL}/images/3-Fold.jpg`,
	},
	{
		title: 'Apply the Strips',
		description: (
			<ul style={{ paddingLeft: '20px' }}>
				<li>Align folded strip 1 with the outer edge of page 1</li>
				<li>
					Attach strips to the page using tape or glue - we suggest a
					permanent glue roller
				</li>
				<li>
					Continue attaching the strips in sequence on the odd pages
					of the book
				</li>
			</ul>
		),
		image: `${process.env.PUBLIC_URL}/images/4-Apply.jpg`,
	},
	{
		title: 'Display Your Finished Book',
		description: (
			<ul style={{ paddingLeft: '20px' }}>
				<li>Once all strips are applied, admire your work!</li>
			</ul>
		),
		image: `${process.env.PUBLIC_URL}/images/5-Display.jpg`,
	},
];

export const HowToSteps = () => {
	const [activeStep, setActiveStep] = useState(0);

	const handleNext = () => {
		setActiveStep((prevActiveStep) => prevActiveStep + 1);
	};

	const handleBack = () => {
		setActiveStep((prevActiveStep) => prevActiveStep - 1);
	};

	const handleReset = () => {
		setActiveStep(0);
	};

	return (
		<Container
			style={{
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				marginTop: '100px',
			}}
		>
			<Typography
				variant="h2"
				style={{
					textAlign: 'center',
					lineHeight: '42px',
					marginBottom: '20px',
				}}
			>
				Create your own{' '}
				<span
					style={{
						background: GRADIENT_WARM,
						WebkitBackgroundClip: 'text',
						WebkitTextFillColor: 'transparent',
					}}
				>
					Fore Edge Book Art
				</span>{' '}
			</Typography>

			<Stepper
				activeStep={activeStep}
				orientation="vertical"
				sx={{ width: '80%' }}
			>
				{STEPS.map((step, index) => (
					<Step key={index} completed={activeStep > index}>
						<StepLabel
							StepIconComponent={(props) =>
								props.completed ? (
									<Check
										style={{
											color: COLOR_GREEN,
											fontSize: '2rem',
										}}
									/>
								) : (
									<CustomStepIcon
										{...props}
										style={{
											color:
												index === activeStep
													? COLOR_GREEN
													: COLOR_ICON_INACTIVE,
										}}
									/>
								)
							}
						>
							<Typography variant="h4" sx={stepLabelTypographySx}>
								{step.title}
							</Typography>
						</StepLabel>
						<StepContent>
							<div
								style={{
									display: 'flex',
									flexDirection: 'row',
									alignItems: 'flex-start',
									flexWrap: 'wrap',
								}}
							>
								<Typography
									variant="h5"
									sx={{
										...stepLabelTypographySx,
										flex: 1,
										minWidth: '50%',
									}}
								>
									{step.description}
								</Typography>
								{step.image && (
									<img
										src={step.image}
										alt={step.title}
										style={{
											maxHeight: '30vh',
											minWidth: '250px',
											boxShadow: SHADOW_IMAGE_CARD,
											borderRadius: '10px',
											marginLeft: '20px',
										}}
									/>
								)}
							</div>
							<div style={{ marginTop: '10px' }}>
								{index !== 0 && (
									<Button
										variant="contained"
										sx={{
											bgcolor: COLOR_SECONDARY_BUTTON,
											color: '#fff',
											marginRight: '10px',
										}}
										onClick={handleBack}
									>
										<Typography
											variant="h5"
											sx={buttonTypographySx}
										>
											Back
										</Typography>
									</Button>
								)}
								{index !== STEPS.length - 1 && (
									<Button
										variant="contained"
										color="primary"
										onClick={handleNext}
									>
										<Typography
											variant="h5"
											sx={buttonTypographySx}
										>
											Next
										</Typography>
									</Button>
								)}
							</div>
						</StepContent>
					</Step>
				))}
			</Stepper>
			{activeStep === STEPS.length && (
				<Paper
					square
					elevation={0}
					sx={{ padding: '20px', marginTop: '20px' }}
				>
					<Typography variant="h5" sx={{ textAlign: 'center' }}>
						All steps completed - you're finished!
					</Typography>
					<Button
						onClick={handleReset}
						sx={{ display: 'block', margin: '20px auto' }}
					>
						Reset
					</Button>
				</Paper>
			)}
		</Container>
	);
};
